/**
 * Navigation : menu mobile (panneau latéral) et mega menu accessible.
 * Aucune dépendance externe. Gère le clavier (Échap), aria-expanded et le
 * clic à l'extérieur. Respecte la sémantique ARIA.
 */

import { AsyncUtils } from './Librairies/utils/asyncUtils.js';

const MOBILE_BREAKPOINT = 1200;

function isMobileViewport(): boolean {
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
}

/** Initialise le bouton hamburger et le panneau de navigation mobile. */
function initMobileNav(): void {
  const toggle = document.querySelector<HTMLButtonElement>('[data-nav-toggle]');
  const nav = document.querySelector<HTMLElement>('[data-primary-nav]');
  const backdrop = document.querySelector<HTMLElement>('[data-nav-backdrop]');

  if (!toggle || !nav) {
    return;
  }

  const setOpen = (open: boolean): void => {
    toggle.setAttribute('aria-expanded', String(open));
    nav.dataset['open'] = String(open);
    if (backdrop) {
      backdrop.dataset['open'] = String(open);
    }
    document.body.classList.toggle('nav-open', open);
  };

  toggle.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    setOpen(!open);
  });

  backdrop?.addEventListener('click', () => setOpen(false));

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
      setOpen(false);
      toggle.focus();
    }
  });

  // Réinitialise l'état lorsque l'on repasse en affichage bureau.
  window.addEventListener('resize', () => {
    if (!isMobileViewport()) {
      setOpen(false);
    }
  });
}

/** Initialise le ou les mega menus (ouverture au clic, fermeture extérieure). */
function initMegaMenu(): void {
  const containers = document.querySelectorAll<HTMLElement>('[data-megamenu]');

  containers.forEach((container) => {
    const toggle = container.querySelector<HTMLButtonElement>('[data-megamenu-toggle]');
    const panel = container.querySelector<HTMLElement>('[data-megamenu-panel]');

    if (!toggle || !panel) {
      return;
    }

    const setOpen = (open: boolean): void => {
      toggle.setAttribute('aria-expanded', String(open));
      panel.dataset['open'] = String(open);
    };

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      setOpen(!open);
    });

    // Survol sur bureau uniquement (progressive enhancement).
    // Un léger délai de fermeture (intention de survol) évite que le menu
    // se ferme lors du déplacement du curseur vers les liens.
    if (!isMobileViewport()) {
      let closeTimer: number | undefined;

      const cancelClose = (): void => {
        AsyncUtils.clearPendingDeferredExecution(closeTimer);
        closeTimer = undefined;
      };

      container.addEventListener('mouseenter', () => {
        cancelClose();
        setOpen(true);
      });

      container.addEventListener('mouseleave', () => {
        cancelClose();
        closeTimer = AsyncUtils.setDeferredExecution(() => setOpen(false), 220);
      });
    }

    document.addEventListener('click', (event: MouseEvent) => {
      if (!container.contains(event.target as Node)) {
        setOpen(false);
      }
    });

    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });
  });
}

export function initNavigation(): void {
  initMobileNav();
  initMegaMenu();
}
